import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';

type CalendarEventKind =
  | 'course_start'
  | 'course_end'
  | 'available'
  | 'deadline'
  | 'assessment_open'
  | 'assessment_close';

interface CalendarEvent {
  id: string;
  kind: CalendarEventKind;
  title: string;
  courseId: string;
  courseName: string;
  sectionItemId?: string;
  at: string;
  kindLabel: string;
  isCompleted: boolean;
}

const WEEKDAYS = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];
const TIME_ZONE = 'America/Santiago';

function calendarDayKey(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

function dayKeyFromDate(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function buildMonthGrid(year: number, month: number) {
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const mondayOffset = startDay === 0 ? 6 : startDay - 1;
  const gridStart = new Date(year, month, 1 - mondayOffset);
  const cells = [];

  for (let i = 0; i < 42; i += 1) {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    cells.push({
      date,
      day: date.getDate(),
      inMonth: date.getMonth() === month,
      key: dayKeyFromDate(date),
    });
  }

  return cells;
}

function eventChipClass(kind: CalendarEventKind): string {
  if (kind === 'course_start' || kind === 'available') return 'cal-event cal-event--primary';
  if (kind === 'deadline' || kind === 'assessment_close') return 'cal-event cal-event--warning';
  if (kind === 'assessment_open') return 'cal-event cal-event--assessment';
  return 'cal-event cal-event--neutral';
}

function eventHref(event: CalendarEvent): string {
  if (event.sectionItemId) {
    return `/mis-cursos/${event.courseId}/play/${event.sectionItemId}`;
  }
  return `/mis-cursos/${event.courseId}`;
}

function formatMonthTitle(year: number, month: number): string {
  const label = new Intl.DateTimeFormat('es-CL', {
    month: 'long',
    year: 'numeric',
    timeZone: TIME_ZONE,
  }).format(new Date(year, month, 15));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatEventWhen(iso: string, now: Date): string {
  const date = new Date(iso);
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.round((date.getTime() - now.getTime()) / dayMs);

  const time = new Intl.DateTimeFormat('es-CL', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  }).format(date);

  if (diffDays === 0) return `Hoy, ${time}`;
  if (diffDays === 1) return `Mañana, ${time}`;
  if (diffDays === -1) return `Ayer, ${time}`;

  const dateLabel = new Intl.DateTimeFormat('es-CL', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: TIME_ZONE,
  }).format(date);

  return `${dateLabel} • ${time}`;
}

function isActiveCalendarEvent(event: CalendarEvent, now: Date): boolean {
  if (event.isCompleted) return false;

  const at = new Date(event.at);
  if (at <= now) {
    return event.kind === 'deadline' || event.kind === 'assessment_close';
  }

  return true;
}

function isPendingEvent(event: CalendarEvent, now: Date): boolean {
  if (!isActiveCalendarEvent(event, now)) return false;
  return new Date(event.at) <= now;
}

function isUpcomingEvent(event: CalendarEvent, now: Date): boolean {
  if (!isActiveCalendarEvent(event, now)) return false;
  return new Date(event.at) > now;
}

export function CalendarPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [viewDate, setViewDate] = useState(() => new Date());
  const [courseFilter, setCourseFilter] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['student', 'calendar'],
    queryFn: () => api<{ data: CalendarEvent[] }>('/student/calendar'),
    enabled: isAuthenticated,
  });

  const now = useMemo(() => new Date(), [viewDate, data]);
  const events = useMemo(() => {
    const rows = (data?.data ?? []).filter((event) => isActiveCalendarEvent(event, now));
    if (!courseFilter) return rows;
    return rows.filter((event) => event.courseId === courseFilter);
  }, [courseFilter, data?.data, now]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      const key = calendarDayKey(event.at);
      const bucket = map.get(key) ?? [];
      bucket.push(event);
      map.set(key, bucket);
    }
    return map;
  }, [events]);

  const courseOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const event of data?.data ?? []) {
      if (!isActiveCalendarEvent(event, now)) continue;
      map.set(event.courseId, event.courseName);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [data?.data, now]);

  const pending = useMemo(
    () =>
      events
        .filter((event) => isPendingEvent(event, now))
        .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
        .slice(0, 5),
    [events, now],
  );

  const upcoming = useMemo(
    () =>
      events
        .filter((event) => isUpcomingEvent(event, now))
        .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
        .slice(0, 8),
    [events, now],
  );

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const todayKey = dayKeyFromDate(new Date());

  const goMonth = (delta: number) => {
    setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));
  };

  return (
    <div className="lms-calendar lms-site-admin-hub-page font-sans">
      <div className="flex flex-col gap-8 lg:flex-row">
        <div className="flex flex-1 flex-col gap-6">
          <header className="flex flex-col gap-2">
            <h1 className="dash-serif text-3xl font-bold text-dash-primary md:text-4xl">Calendario de Actividades</h1>
            <p className="text-sm text-dash-on-surface-variant md:text-base">
              Visualiza tus entregas, exámenes y actividades programadas.
            </p>
          </header>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {(error as Error).message}
            </p>
          )}

          <div className="overflow-hidden rounded-xl border border-dash-outline-variant bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-dash-outline-variant bg-dash-surface-container-low px-4 py-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg p-2 text-dash-primary transition-colors hover:bg-dash-surface-variant"
                  aria-label="Mes anterior"
                  onClick={() => goMonth(-1)}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    chevron_left
                  </span>
                </button>
                <h2 className="min-w-[180px] text-center text-base font-semibold text-dash-primary">
                  {formatMonthTitle(year, month)}
                </h2>
                <button
                  type="button"
                  className="rounded-lg p-2 text-dash-primary transition-colors hover:bg-dash-surface-variant"
                  aria-label="Mes siguiente"
                  onClick={() => goMonth(1)}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    chevron_right
                  </span>
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-dash-primary px-4 py-2 text-sm font-medium text-dash-primary transition-colors hover:bg-dash-surface-variant"
                  onClick={() => setViewDate(new Date())}
                >
                  Hoy
                </button>
                {courseOptions.length > 1 && (
                  <select
                    className="rounded-lg border border-dash-outline-variant bg-white px-4 py-2 text-sm font-medium text-dash-on-surface"
                    value={courseFilter}
                    onChange={(event) => setCourseFilter(event.target.value)}
                  >
                    <option value="">Todos los cursos</option>
                    {courseOptions.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="grid grid-cols-7 border-b border-dash-outline-variant bg-dash-surface-container-low">
              {WEEKDAYS.map((label) => (
                <div key={label} className="p-2 text-center text-xs font-bold tracking-wide text-dash-on-surface-variant">
                  {label}
                </div>
              ))}
            </div>

            {isLoading || authLoading ? (
              <div className="flex justify-center py-16">
                <Loader />
              </div>
            ) : (
              <div className="cal-grid">
                {grid.map((cell) => {
                  const dayEvents = eventsByDay.get(cell.key) ?? [];
                  const isToday = cell.key === todayKey;
                  return (
                    <div
                      key={cell.key}
                      className={`cal-cell ${cell.inMonth ? '' : 'cal-cell--inactive'} ${isToday ? 'cal-cell--today' : ''}`}
                    >
                      <span className="cal-day-number text-sm font-medium">{cell.day}</span>
                      <div className="cal-cell-events">
                        {dayEvents.slice(0, 3).map((event) => (
                          <Link
                            key={event.id}
                            to={eventHref(event)}
                            className={eventChipClass(event.kind)}
                            title={`${event.kindLabel}: ${event.title}`}
                          >
                            {event.title}
                          </Link>
                        ))}
                        {dayEvents.length > 3 && (
                          <p className="text-xs text-dash-on-surface-variant">+{dayEvents.length - 3} más</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <aside className="flex w-full flex-col gap-6 lg:w-80">
          <section className="flex flex-col gap-4 rounded-xl border border-dash-outline-variant bg-white p-4 shadow-sm">
            <h3 className="flex items-center gap-2 text-base font-semibold text-dash-primary">
              <span className="material-symbols-outlined text-[#ca8a04]" aria-hidden="true">
                warning
              </span>
              Actividades Pendientes
            </h3>
            {pending.length === 0 ? (
              <p className="text-sm text-dash-on-surface-variant">No tienes actividades vencidas pendientes.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {pending.map((event) => (
                  <Link key={event.id} to={eventHref(event)} className="cal-pending-card block no-underline">
                    <div className="mb-1 text-sm font-semibold text-[#854d0e]">{event.title}</div>
                    <div className="text-xs text-[#a16207]">
                      {formatEventWhen(event.at, now)} • {event.courseName}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section className="flex flex-col gap-4 rounded-xl border border-dash-outline-variant bg-white p-4 shadow-sm">
            <h3 className="flex items-center gap-2 text-base font-semibold text-dash-primary">
              <span className="material-symbols-outlined text-dash-primary-container" aria-hidden="true">
                event
              </span>
              Próximas Actividades
            </h3>
            {upcoming.length === 0 ? (
              <p className="text-sm text-dash-on-surface-variant">No hay actividades programadas próximamente.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {upcoming.map((event) => (
                  <Link key={event.id} to={eventHref(event)} className="cal-upcoming-card block no-underline">
                    <div className="mb-1 flex items-center gap-2">
                      <span
                        className={`cal-dot ${event.kind === 'deadline' || event.kind === 'assessment_close' ? 'cal-dot--neutral' : 'cal-dot--primary'}`}
                        aria-hidden="true"
                      />
                      <div className="text-sm font-semibold text-dash-on-surface">{event.title}</div>
                    </div>
                    <div className="text-xs text-dash-on-surface-variant">
                      {formatEventWhen(event.at, now)} • {event.courseName}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
